/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { breakpoints } from '@coursera/cds-core';

import { usePageContext } from 'bundles/page-config-common/contexts/PageContext';

type PropsForHomeSection = {
  children: React.ReactNode;
  spacing?: number;
  className?: string;
  dataTestId?: string;
  onlyTopPadding?: boolean;
};

const styles = {
  separator: css`
    &::before,
    &::after {
      content: '';
      display: table;
    }
  `,
  marginEnterprise: (spacing: number) => css`
    padding: ${spacing + 16}px 0;

    ${breakpoints.down('sm')} {
      padding: ${spacing + 12}px 0;
    }
  `,
  marginConsumer: (spacing: number) => css`
    padding: ${spacing + 16}px 0;

    ${breakpoints.down('sm')} {
      padding: ${spacing + 12}px 0;
    }
  `,
  onlyTopPadding: (spacing: number) => css`
    padding-bottom: 0;
    padding-top: ${spacing + 32}px;

    ${breakpoints.down('sm')} {
      padding-top: ${spacing + 24}px;
    }
  `,
};

/**
 * @description Custom margins for page sections on the home page
 */
const HomeSection = ({
  children,
  spacing,
  className,
  dataTestId,
  onlyTopPadding,
}: PropsForHomeSection): JSX.Element | null => {
  const pageContext = usePageContext();

  if (pageContext?.isConsumer) {
    return (
      <div className={className} css={styles.separator} data-testid={dataTestId}>
        <div css={styles.marginConsumer(spacing || 0)} data-testid="margin-div" className="HomeSection">
          {children}
        </div>
      </div>
    );
  } else {
    return (
      <div className={className} css={styles.separator} data-testid={dataTestId}>
        <div
          css={[styles.marginEnterprise(spacing || 0), onlyTopPadding && styles.onlyTopPadding(spacing || 0)]}
          data-testid="margin-div"
          className="HomeSection"
        >
          {children}
        </div>
      </div>
    );
  }
};

export default HomeSection;
